import styled, { keyframes } from 'styled-components';

// ===== PALETA =====
// Azul marinho: #0B1929 (fundo), #1A3050 (fundo elevado), #2A4A6B (accent)
// Branco: #FFFFFF / rgba(255, 255, 255, ...) (accent principal)
// Azul claro sutil: #8AACC8 (texto secundario, bordas)

// ===== ANIMACOES =====

export const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

export const elegantEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(15px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

export const elegantExit = keyframes`
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(-10px) scale(0.97);
  }
`;

// Mantidos para compatibilidade de nomes (usados nas classes CSS)
export const slideInFromRight = elegantEnter;
export const slideOutToLeft = elegantExit;
export const slideInFromLeft = elegantEnter;
export const slideOutToRight = elegantExit;

// ===== HERO =====

export const HeroHeadline = styled.div`
  position: fixed;
  top: 18%;
  left: 0;
  width: 100%;
  z-index: 10;
  padding: 0 36px;
  text-align: center;
  pointer-events: none;

  h1 {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(1.6rem, 4.5vw, 2.4rem);
    font-weight: 600;
    color: #FFFFFF;
    line-height: 1.4;
    letter-spacing: 2px;
    text-transform: uppercase;
    margin: 0;
    opacity: 0;
    animation: ${fadeInUp} 1.4s cubic-bezier(0.16, 1, 0.3, 1) 0.8s forwards;
    text-shadow:
      0 0 4px rgba(0, 0, 0, 1),
      0 0 8px rgba(0, 0, 0, 1),
      0 0 16px rgba(0, 0, 0, 1),
      0 0 32px rgba(0, 0, 0, 1),
      0 0 64px rgba(0, 0, 0, 1),
      0 0 120px rgba(0, 0, 0, 0.95),
      0 0 200px rgba(0, 0, 0, 0.9),
      0 0 300px rgba(0, 0, 0, 0.7);
  }

  @media (max-width: 768px) {
    top: 14%;
    padding: 0 28px;
  }
`;

// ===== VIDEO SECTION =====

export const PageContainer = styled.div`
  width: 100%;
  min-height: ${props => props.$scrollHeight}px;
  background: #0B1929;
  position: relative;
`;

export const VideoContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0B1929;
  z-index: 1;
`;

export const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const IntroHeadline = styled.h1`
  position: fixed;
  top: 20%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  margin: 0;
  padding: 0 24px;
  max-width: 900px;
  width: 100%;
  text-align: center;
  color: #ffffff;
  font-family: 'Playfair Display', 'Georgia', serif;
  font-size: clamp(28px, 5vw, 56px);
  font-weight: 500;
  line-height: 1.15;
  letter-spacing: -0.5px;
  text-shadow:
    0 2px 8px rgba(0, 0, 0, 0.9),
    0 4px 24px rgba(0, 0, 0, 0.85),
    0 8px 40px rgba(0, 0, 0, 0.7);
  animation: headlineFadeIn 1.4s ease-out both;
  pointer-events: none;

  em {
    font-style: italic;
    font-weight: 600;
    background: linear-gradient(135deg, #d4b87a 0%, #f5e6b8 50%, #d4b87a 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  @media (max-width: 768px) {
    top: 14%;
    padding: 0 20px;
    font-size: clamp(24px, 7vw, 36px);
  }

  @keyframes headlineFadeIn {
    0% {
      opacity: 0;
      transform: translateX(-50%) translateY(-12px);
    }
    100% {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
`;

export const ScrollHint = styled.div`
  position: fixed;
  bottom: 50px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  z-index: 10;
  animation: fadeFloat 3s ease-in-out infinite;

  &::before {
    content: 'Role para descobrir';
    color: #ffffff;
    font-size: 15px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 5px;
    font-family: 'Inter', sans-serif;
    text-shadow: 0 2px 12px rgba(0, 0, 0, 0.6);
  }

  &::after {
    content: '';
    width: 1px;
    height: 40px;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0.5), transparent);
    animation: lineGrow 2.5s ease-in-out infinite;
  }

  @keyframes fadeFloat {
    0%, 100% {
      opacity: 0.85;
      transform: translateX(-50%) translateY(0);
    }
    50% {
      opacity: 1;
      transform: translateX(-50%) translateY(-4px);
    }
  }

  @keyframes lineGrow {
    0%, 100% { height: 40px; opacity: 0.5; }
    50% { height: 48px; opacity: 0.8; }
  }
`;

// ===== SCROLL NAV BAR =====

export const ScrollNavBar = styled.div`
  position: fixed;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 50;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.5s ease;
  pointer-events: ${props => props.$visible ? 'auto' : 'none'};
  padding: 12px 8px;
  background: rgba(0, 0, 0, 0.35);
  border-radius: 20px;
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);

  @media (max-width: 768px) {
    right: 14px;
    padding: 10px 6px;
  }
`;

export const ScrollTrack = styled.div`
  position: relative;
  width: 3px;
  height: 220px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  overflow: visible;
  cursor: grab;

  &:active { cursor: grabbing; }

  @media (max-width: 768px) {
    height: 180px;
  }
`;

export const ScrollProgressBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.85) 0%, rgba(138, 172, 200, 0.5) 100%);
  border-radius: 4px;
  height: ${props => props.$progress}%;
  transition: height 0.15s cubic-bezier(0.4, 0, 0.2, 1);
`;

export const Marker = styled.button`
  position: absolute;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1.5px solid ${props => props.$active ? 'rgba(255, 255, 255, 0.85)' : 'rgba(255, 255, 255, 0.3)'};
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.2)' : 'rgba(11, 25, 41, 0.6)'};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  left: 50%;
  transform: translateX(-50%) scale(${props => props.$active ? 1.15 : 1});
  top: ${props => props.$position}%;
  z-index: 2;

  &:hover {
    border-color: rgba(255, 255, 255, 0.7);
    background: rgba(255, 255, 255, 0.15);
    transform: translateX(-50%) scale(1.3);
  }

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: ${props => props.$active ? '4px' : '0'};
    height: ${props => props.$active ? '4px' : '0'};
    background: rgba(255, 255, 255, 0.85);
    border-radius: 50%;
    transition: all 0.3s ease;
  }

  @media (max-width: 768px) {
    width: 10px;
    height: 10px;
  }
`;

export const MarkerTooltip = styled.span`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.75);
  color: rgba(255, 255, 255, 0.85);
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 4px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease, transform 0.2s ease;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  font-family: 'Inter', sans-serif;

  ${Marker}:hover & {
    opacity: 1;
    transform: translateY(-50%) translateX(-5px);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

// ===== QUIZ SECTION =====

export const QuizOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  z-index: 200;
  background: rgba(8, 18, 32, 0.92);
  backdrop-filter: blur(20px);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.5s ease-out forwards;
`;

export const QuizContainer = styled.div`
  width: 100%;
  max-width: 560px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;

  &.enter-right { animation: ${elegantEnter} 400ms cubic-bezier(0.25, 0.1, 0.25, 1) both; }
  &.enter-left { animation: ${elegantEnter} 400ms cubic-bezier(0.25, 0.1, 0.25, 1) both; }
  &.exit-left { animation: ${elegantExit} 350ms cubic-bezier(0.25, 0.1, 0.25, 1) both; }
  &.exit-right { animation: ${elegantExit} 350ms cubic-bezier(0.25, 0.1, 0.25, 1) both; }
`;

export const QuizTitle = styled.h1`
  font-family: 'Playfair Display', serif;
  font-size: 1.8rem;
  font-weight: 600;
  color: #FFFFFF;
  text-align: center;
  margin-bottom: 8px;
  letter-spacing: 0.5px;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

export const QuizSubtitle = styled.p`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.55);
  text-align: center;
  margin-bottom: 40px;
  font-family: 'Inter', sans-serif;
  font-weight: 300;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 0.88rem;
    margin-bottom: 32px;
  }
`;

export const OptionPill = styled.button`
  width: 100%;
  padding: 20px 28px;
  margin-bottom: 14px;
  border-radius: 8px;
  border: 1px solid ${props => props.$selected ? 'rgba(255, 255, 255, 0.35)' : 'rgba(255, 255, 255, 0.08)'};
  background: ${props => props.$selected
    ? 'rgba(255, 255, 255, 0.07)'
    : 'rgba(255, 255, 255, 0.02)'};
  color: ${props => props.$selected ? '#FFFFFF' : 'rgba(255, 255, 255, 0.8)'};
  font-size: 1rem;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.25s ease;
  display: flex;
  align-items: center;
  gap: 16px;
  text-align: left;
  letter-spacing: 0.2px;

  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.04);
    color: #FFFFFF;
  }

  &:active {
    background: rgba(255, 255, 255, 0.06);
  }

  @media (max-width: 768px) {
    padding: 18px 22px;
    font-size: 0.95rem;
  }
`;

export const ProgressDots = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 40px;
`;

export const Dot = styled.div`
  width: ${props => props.$active ? '20px' : '6px'};
  height: 6px;
  border-radius: 3px;
  background: ${props =>
    props.$active
      ? 'rgba(255, 255, 255, 0.85)'
      : props.$completed
        ? 'rgba(138, 172, 200, 0.5)'
        : 'rgba(255, 255, 255, 0.12)'};
  transition: all 0.3s ease;
`;

export const BackButton = styled.button`
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.5);
  padding: 10px 24px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  transition: all 0.25s ease;
  margin-top: 20px;
  letter-spacing: 0.3px;

  &:hover {
    border-color: rgba(255, 255, 255, 0.25);
    color: rgba(255, 255, 255, 0.8);
  }
`;

// ===== RESULTS & CTA =====

export const ResultsContainer = styled.div`
  width: 100%;
  max-width: 560px;
  padding: 20px;
`;

export const EmpCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 14px;
  transition: all 0.25s ease;
  animation: ${fadeInUp} 0.5s ease-out forwards;
  animation-delay: ${props => props.$delay || '0s'};
  opacity: 0;

  &:hover {
    border-color: rgba(138, 172, 200, 0.3);
    background: rgba(138, 172, 200, 0.04);
  }
`;

export const EmpName = styled.h3`
  font-family: 'Playfair Display', serif;
  font-size: 1.15rem;
  font-weight: 600;
  color: #FFFFFF;
  margin-bottom: 4px;
`;

export const EmpHighlight = styled.span`
  font-size: 0.7rem;
  color: rgba(138, 172, 200, 0.9);
  background: rgba(138, 172, 200, 0.08);
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid rgba(138, 172, 200, 0.2);
  margin-left: 12px;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

export const EmpDescription = styled.p`
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.88rem;
  margin-top: 10px;
  font-family: 'Inter', sans-serif;
  font-weight: 300;
  line-height: 1.5;
`;

export const CTAButton = styled.button`
  width: 100%;
  padding: 18px 32px;
  border-radius: 8px;
  border: none;
  background: #FFFFFF;
  color: #0B1929;
  font-size: 0.95rem;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 3px;
  margin-top: 8px;

  &:hover {
    background: rgba(255, 255, 255, 0.9);
  }

  &:active {
    transform: scale(0.99);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    font-size: 0.88rem;
    padding: 16px 24px;
  }
`;

export const SecondaryButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 24px;
  border-radius: 8px;
  border: 1px solid rgba(37, 211, 102, 0.3);
  background: transparent;
  color: rgba(37, 211, 102, 0.85);
  font-size: 0.9rem;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.25s ease;
  margin-top: 12px;

  &:hover {
    background: rgba(37, 211, 102, 0.06);
    border-color: rgba(37, 211, 102, 0.5);
  }
`;

export const LoadingSpinner = styled.span`
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(11, 25, 41, 0.3);
  border-top-color: #0B1929;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  margin-left: 8px;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
